// src/services/products.service.js

import { env } from "../config/env";
import { createClient } from "./http";
import { getAuthToken } from "../pages/AdminAuth/components/authStore";

const api = createClient(env.CATALOG_BASE_URL);

function getTokenHeader() {
  const token = getAuthToken?.() || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function pickArray(value) {
  return Array.isArray(value) ? value : [];
}

function buildImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(env.CATALOG_BASE_URL || "").replace(/\/+$/, "");
  const cleanPath = String(path).replace(/^\/+/, "");
  return base ? `${base}/${cleanPath}` : `/${cleanPath}`;
}

function normalizeImage(item = {}, index = 0) {
  const url = item?.url || item?.imageUrl || item?.image_url || "";
  return {
    id: item?.id || `${url}-${index}`,
    url: buildImageUrl(url),
    rawUrl: url,
    isPrimary: Boolean(item?.isPrimary ?? item?.is_primary ?? index === 0),
    sortOrder: item?.sortOrder ?? item?.sort_order ?? index,
  };
}

function normalizeVariant(item = {}, index = 0) {
  const id = item?.id || item?._id || "";
  const name = item?.variantName || item?.variant_name || item?.name || item?.label || "";
  const unitValue = item?.unitValue || item?.unit_value || item?.packSize || item?.pack_size || item?.weightValue || item?.weight_value || "";
  const unitSymbol = item?.unitSymbol || item?.unit_symbol || item?.weightUnitSymbol || item?.weight_unit_symbol || "";
  const generatedLabel = [unitValue, unitSymbol].filter(Boolean).join(" ").trim();
  const images = pickArray(item?.images).map(normalizeImage);
  const primaryImage = images.find((img) => img?.isPrimary)?.url || images[0]?.url || buildImageUrl(item?.imageUrl || item?.image_url || "");

  return {
    id,
    variantName: name,
    variant_name: name,
    name: name || generatedLabel || `Variant ${index + 1}`,
    label: item?.label || item?.packLabel || item?.pack_label || name || generatedLabel || `Variant ${index + 1}`,
    sku: item?.sku || item?.variantSku || item?.variant_sku || "",
    variantSku: item?.variantSku || item?.variant_sku || item?.sku || "",
    variant_sku: item?.variantSku || item?.variant_sku || item?.sku || "",
    barcode: item?.barcode || "",
    packLabel: item?.packLabel || item?.pack_label || "",
    pack_label: item?.packLabel || item?.pack_label || "",
    unitValue,
    unit_value: unitValue,
    unitSymbol,
    unit_symbol: unitSymbol,
    weightValue: item?.weightValue || item?.weight_value || "",
    weight_value: item?.weightValue || item?.weight_value || "",
    weightUnitSymbol: item?.weightUnitSymbol || item?.weight_unit_symbol || "",
    weight_unit_symbol: item?.weightUnitSymbol || item?.weight_unit_symbol || "",
    shortDescription: item?.shortDescription || item?.short_description || "",
    short_description: item?.shortDescription || item?.short_description || "",
    description: item?.description || "",
    variantGroup: item?.variantGroup || item?.variant_group || "",
    variant_group: item?.variantGroup || item?.variant_group || "",
    variantValue: item?.variantValue || item?.variant_value || "",
    variant_value: item?.variantValue || item?.variant_value || "",
    imageUrl: primaryImage,
    image_url: primaryImage,
    images,
    isDefault: Boolean(item?.isDefault ?? item?.is_default ?? index === 0),
    is_default: Boolean(item?.isDefault ?? item?.is_default ?? index === 0),
    isActive: item?.isActive ?? item?.is_active ?? true,
    is_active: item?.isActive ?? item?.is_active ?? true,
    sortOrder: item?.sortOrder ?? item?.sort_order ?? index,
    sort_order: item?.sortOrder ?? item?.sort_order ?? index,
    mrp: item?.mrp ?? null,
    sellingPrice: item?.sellingPrice ?? item?.selling_price ?? null,
    selling_price: item?.sellingPrice ?? item?.selling_price ?? null,
    prices: pickArray(item?.prices),
  };
}

function pickBestPrice(prices = []) {
  const list = pickArray(prices);
  if (!list.length) return {};
  const active = list.filter((x) => x?.active);
  const source = active.length ? active : list;
  return source[source.length - 1] || {};
}

function normalizeProduct(item = {}) {
  const images = pickArray(item?.images).map(normalizeImage);
  const variantsSource = pickArray(item?.variants).length
    ? pickArray(item?.variants)
    : [
      {
        id: item?.defaultVariantId || "",
        variantName:
          item?.variantName ||
          [item?.weightValue || item?.weight_value, item?.weightUnitSymbol || item?.weight_unit_symbol]
            .filter(Boolean)
            .join(" ") ||
          "Default",
        weightValue: item?.weightValue || item?.weight_value || "",
        weightUnitSymbol: item?.weightUnitSymbol || item?.weight_unit_symbol || "",
        shortDescription: item?.shortDescription || item?.short_description || "",
        description: item?.description || "",
        imageUrl: item?.image_url || item?.imageUrl || "",
        isDefault: true,
        mrp: item?.mrp ?? null,
        sellingPrice: item?.sellingPrice ?? item?.selling_price ?? null,
      },
    ];
  const variants = variantsSource.map(normalizeVariant);
  const defaultVariant = variants.find((v) => v?.isDefault) || variants[0] || null;
  const prices = pickArray(item?.prices);
  const price = pickBestPrice(defaultVariant?.prices?.length ? defaultVariant.prices : prices);
  const primaryImage =
    images.find((img) => img?.isPrimary)?.url ||
    images[0]?.url ||
    buildImageUrl(item?.image_url || item?.imageUrl || "");

  return {
    ...item,
    id: item?.id || item?._id || "",
    name: item?.name || "",
    sku: item?.sku || "",
    barcode: item?.barcode || "",
    slug: item?.slug || "",
    shortDescription: item?.shortDescription || item?.short_description || "",
    description: item?.description || "",
    imageUrl: primaryImage,
    image_url: primaryImage,
    categoryName: item?.categoryName || item?.category_name || item?.category?.name || "",
    brandName: item?.brandName || item?.brand_name || item?.brand?.name || "",
    unitName: item?.unitName || item?.unit_name || item?.unit?.name || "",
    unitSymbol: item?.unitSymbol || item?.unit_symbol || item?.unit?.symbol || "",
    taxPercent: item?.taxPercent ?? item?.tax_percent ?? "",
    weightValue:
      item?.weightValue ??
      item?.weight_value ??
      item?.defaultWeightValue ??
      item?.default_weight_value ??
      defaultVariant?.weightValue ??
      defaultVariant?.weight_value ??
      "",

    weightUnitSymbol:
      item?.weightUnitSymbol ??
      item?.weight_unit_symbol ??
      item?.defaultWeightUnitSymbol ??
      item?.default_weight_unit_symbol ??
      defaultVariant?.weightUnitSymbol ??
      defaultVariant?.weight_unit_symbol ??
      "",
    mrp: price?.mrp ?? item?.mrp ?? defaultVariant?.mrp ?? "",
    sellingPrice: price?.sellingPrice ?? price?.selling_price ?? item?.sellingPrice ?? item?.selling_price ?? defaultVariant?.sellingPrice ?? "",
    purchasePrice: item?.purchasePrice ?? item?.purchase_price ?? "",
    purchaseCurrency: item?.purchaseCurrency ?? item?.purchase_currency ?? "INR",
    isActive: item?.isActive ?? item?.is_active ?? true,
    status: item?.status || "",
    images,
    prices,
    variants,
    defaultVariant,
    defaultVariantId: defaultVariant?.id || item?.defaultVariantId || "",
  };
}

async function uploadFiles(url, fieldName, files = []) {
  if (!files.length) return null;
  const form = new FormData();
  files.forEach((file) => form.append(fieldName, file));
  const token = getAuthToken?.() || "";
  const res = await fetch(`${env.CATALOG_BASE_URL}${url}`, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.message || "Image upload failed");
  return json;
}

export const productsService = {
  async listProducts({
    page = 1,
    limit = 20,
    warehouseId = "",
    q = "",
    status = "",
    categoryId = "",
    subcategoryId = "",
    brandId = "",
  } = {}) {
    const offset = Math.max(0, (Number(page) - 1) * Number(limit));
    const params = { limit, offset };
    if (warehouseId) params.warehouseId = warehouseId;
    if (q) params.q = q;
    if (status) params.status = status;
    if (categoryId) params.categoryId = categoryId;
    if (subcategoryId) params.subcategoryId = subcategoryId;
    if (brandId) params.brandId = brandId;

    const res = await api.get("/api/admin/products", {
      params,
      headers: getTokenHeader(),
    });
    const raw = res?.data ?? res ?? {};
    const items = pickArray(raw?.items || raw?.data || raw?.rows || raw?.products || raw);
    return {
      ...raw,
      items: items.map(normalizeProduct),
    };
  },

  async getProductById(id, { warehouseId = "" } = {}) {
    if (!id) throw new Error("Product id is required");
    const params = {};
    if (warehouseId) params.warehouseId = warehouseId;
    const res = await api.get(`/api/admin/products/${id}`, {
      params,
      headers: getTokenHeader(),
    });
    const raw = res?.data ?? res ?? {};
    const item = normalizeProduct(raw?.item || raw?.data?.item || raw?.product || raw?.data || raw);
    return { ...raw, item, product: item };
  },

  async updateProduct(id, payload) {
    if (!id) throw new Error("Product id is required");
    return api.put(`/api/admin/products/${id}`, payload, {
      headers: getTokenHeader(),
    });
  },

  async getVariants(productId) {
    if (!productId) throw new Error("Product id is required");
    const res = await api.get(`/api/admin/products/${productId}/variants`, {
      headers: getTokenHeader(),
    });
    const raw = res?.data ?? res ?? {};
    const items = pickArray(raw?.items || raw?.data || raw?.rows || raw);
    return items.map(normalizeVariant);
  },

  async updateVariants(productId, variants = []) {
    if (!productId) throw new Error("Product id is required");
    return api.put(`/api/admin/products/${productId}/variants`, { variants }, {
      headers: getTokenHeader(),
    });
  },

  async setDefaultVariant(productId, variantId) {
    if (!productId) throw new Error("Product id is required");
    if (!variantId) throw new Error("Variant id is required");
    return api.patch(`/api/admin/products/${productId}/variants/${variantId}/default`, {}, {
      headers: getTokenHeader(),
    });
  },

  async toggleActive(id, isActive) {
    if (!id) throw new Error("Product id is required");
    return api.patch(
      `/api/admin/products/${id}/activate`,
      { isActive: Boolean(isActive) },
      { headers: getTokenHeader() }
    );
  },
  async setActive(id, isActive) { return this.toggleActive(id, isActive); },
  async setProductActive(id, isActive) { return this.toggleActive(id, isActive); },
  async deleteProduct(id) {
    if (!id) throw new Error("Product id is required");
    return api.del(`/api/admin/products/${id}`, { headers: getTokenHeader() });
  },
  async upsertProduct(payload) {
    return api.post("/api/admin/products", payload, { headers: getTokenHeader() });
  },
  async previewByBarcode(barcode) {
    if (!barcode) throw new Error("Barcode is required");
    return api.get(`/api/admin/products/by-barcode/${encodeURIComponent(barcode)}/preview`);
  },
  async importByBarcode(barcode) {
    if (!barcode) throw new Error("Barcode is required");
    return api.post(`/api/admin/products/by-barcode/${encodeURIComponent(barcode)}`);
  },
  async listProductImages(productId) {
    if (!productId) throw new Error("Product id is required");
    return api.get(`/api/admin/products/${productId}/images`, { headers: getTokenHeader() });
  },
  async uploadPrimaryImage(productId, file) {
    if (!productId) throw new Error("Product id is required");
    if (!file) throw new Error("Image file is required");
    const form = new FormData();
    form.append("image", file);
    const token = getAuthToken?.() || "";
    const res = await fetch(`${env.CATALOG_BASE_URL}/api/admin/products/${productId}/images/primary`, {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || "Image upload failed");
    return json;
  },
  async uploadProductImages(productId, files = [], primaryIndex = null) {
    if (!productId) throw new Error("Product id is required");
    if (!files.length) return null;
    const json = await uploadFiles(`/api/admin/products/${productId}/images`, "images", files);
    const images = pickArray(json?.items || json?.images || json?.data || []);
    if (primaryIndex != null && images[primaryIndex]?.id) {
      await this.setPrimaryProductImage(productId, images[primaryIndex].id);
    }
    return json;
  },
  async setPrimaryProductImage(productId, imageId) {
    if (!productId) throw new Error("Product id is required");
    if (!imageId) throw new Error("Image id is required");
    return api.patch(`/api/admin/products/${productId}/images/${imageId}/primary`, {}, { headers: getTokenHeader() });
  },
  async deleteProductImage(productId, imageId) {
    if (!productId) throw new Error("Product id is required");
    if (!imageId) throw new Error("Image id is required");
    return api.del(`/api/admin/products/${productId}/images/${imageId}`, { headers: getTokenHeader() });
  },
  async listVariantImages(productId, variantId) {
    if (!productId) throw new Error("Product id is required");
    if (!variantId) throw new Error("Variant id is required");
    return api.get(`/api/admin/products/${productId}/variants/${variantId}/images`, { headers: getTokenHeader() });
  },
  async uploadVariantImages(productId, variantId, files = [], primaryIndex = null) {
    if (!productId) throw new Error("Product id is required");
    if (!variantId) throw new Error("Variant id is required");
    if (!files.length) return null;
    const json = await uploadFiles(`/api/admin/products/${productId}/variants/${variantId}/images`, "images", files);
    const images = pickArray(json?.items || json?.images || json?.data || []);
    if (primaryIndex != null && images[primaryIndex]?.id) {
      await this.setPrimaryVariantImage(productId, variantId, images[primaryIndex].id);
    }
    return json;
  },
  async setPrimaryVariantImage(productId, variantId, imageId) {
    if (!productId) throw new Error("Product id is required");
    if (!variantId) throw new Error("Variant id is required");
    if (!imageId) throw new Error("Image id is required");
    return api.patch(`/api/admin/products/${productId}/variants/${variantId}/images/${imageId}/primary`, {}, { headers: getTokenHeader() });
  },
  async deleteVariantImage(productId, variantId, imageId) {
    if (!productId) throw new Error("Product id is required");
    if (!variantId) throw new Error("Variant id is required");
    if (!imageId) throw new Error("Image id is required");
    return api.del(`/api/admin/products/${productId}/variants/${variantId}/images/${imageId}`, { headers: getTokenHeader() });
  },
  async listWarehouseAssignmentProducts({ warehouseId, q = "", page = 1, limit = 20 } = {}) {
    if (!warehouseId) throw new Error("warehouseId is required");
    const offset = Math.max(0, (Number(page) - 1) * Number(limit));
    return api.get("/api/admin/products/warehouse-assignment/list", {
      params: { warehouseId, q, limit, offset },
      headers: getTokenHeader(),
    });
  },
  async commitWarehouseAssignment({
    warehouseId,
    mode = "replace",
    variantIds = [],
    productIds = [],
    visibleVariantIds = [],
    productNames = [],
    warehouseName = "",
  }) {
    if (!warehouseId) throw new Error("warehouseId is required");

    return api.post(
      "/api/admin/products/warehouse-assignment/commit",
      {
        warehouseId,
        mode,
        variantIds,
        productIds,
        visibleVariantIds,
        productNames,
        warehouseName,
      },
      { headers: getTokenHeader() }
    );
  },
};
